"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { toast } from "sonner"
import {
  buildProductReviewPermissionQueryKey,
  buildProductReviewsQueryKey,
  type ProductReviewItem,
  type ProductReviewPermissionResponse,
  type ProductReviewsResponse,
  submitProductReview,
} from "@/api/product-reviews"
import { ReviewItem } from "@/components/shared/reviews/item"
import {
  calculateReviewsSummary,
  ReviewsSummary,
} from "@/components/shared/reviews/summary"
import type { MediaAttachment } from "@/services/media-upload-service"
import {
  DEFAULT_REVIEW_AVATAR,
  type PendingProductReviewItem,
  ReviewsEmptyState,
} from "."
import {
  getProductReviewPermissionQueryOptions,
  getProductReviewsQueryOptions,
} from "./query"
import { ReviewComposer } from "./review-composer"

interface ProductReviewsTabClientProps {
  productId: number
  sessionCookie?: string
  writeReview?: boolean
}

interface CreateReviewInput {
  content: string
  rating: 1 | 2 | 3 | 4 | 5
  attachments: MediaAttachment[]
}

interface CreateReviewMutationContext {
  optimisticReviewId: number
  previousPermission?: ProductReviewPermissionResponse
  previousReviews?: ProductReviewsResponse
}

const REVIEW_SUBMIT_ERROR_MESSAGE = "Nao foi possivel enviar sua avaliacao."

const createOptimisticReview = ({
  author,
  content,
  rating,
}: {
  author: string
  content: string
  rating: 1 | 2 | 3 | 4 | 5
}): PendingProductReviewItem => ({
  author,
  avatar: DEFAULT_REVIEW_AVATAR,
  content,
  date: new Date().toISOString(),
  id: -Date.now(),
  isPending: true,
  rating,
  verified: true,
})

const getReviewSubmitErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const responseMessage = error.response?.data?.message

    if (typeof responseMessage === "string" && responseMessage.length > 0) {
      return responseMessage
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return REVIEW_SUBMIT_ERROR_MESSAGE
}

export function ProductReviewsTabClient({
  productId,
  sessionCookie,
  writeReview = false,
}: ProductReviewsTabClientProps): React.ReactNode {
  const queryClient = useQueryClient()
  const reviewsQueryOptions = getProductReviewsQueryOptions(productId)
  const permissionQueryOptions =
    getProductReviewPermissionQueryOptions(productId)
  const reviewsQuery = useQuery(reviewsQueryOptions)
  const permissionQuery = useQuery({
    ...permissionQueryOptions,
    retry: false,
  })

  const restoreQueryData = <T,>(
    queryKey: readonly unknown[],
    previousData: T | undefined
  ): void => {
    if (previousData === undefined) {
      queryClient.removeQueries({ queryKey, exact: true })
      return
    }

    queryClient.setQueryData(queryKey, previousData)
  }

  const createReview = useMutation({
    mutationFn: (input: CreateReviewInput) =>
      submitProductReview(productId, input),
    onMutate: async (input): Promise<CreateReviewMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: buildProductReviewsQueryKey(productId),
        }),
        queryClient.cancelQueries({
          queryKey: buildProductReviewPermissionQueryKey(productId),
        }),
      ])

      const previousReviews = queryClient.getQueryData<ProductReviewsResponse>(
        reviewsQueryOptions.queryKey
      )
      const previousPermission =
        queryClient.getQueryData<ProductReviewPermissionResponse>(
          permissionQueryOptions.queryKey
        )
      const optimisticReview = createOptimisticReview({
        author: permissionQuery.data?.user?.name ?? "Voce",
        content: input.content,
        rating: input.rating,
      })
      const currentReviews = previousReviews ?? {
        ok: true,
        reviews: [],
        summary: { average: 0, distribution: [], total: 0 },
      }
      const nextReviews = [optimisticReview, ...currentReviews.reviews]

      queryClient.setQueryData<ProductReviewsResponse>(
        reviewsQueryOptions.queryKey,
        {
          ...currentReviews,
          reviews: nextReviews,
          summary: calculateReviewsSummary(nextReviews),
        }
      )

      queryClient.setQueryData<ProductReviewPermissionResponse>(
        permissionQueryOptions.queryKey,
        {
          ...previousPermission,
          canReview: false,
          ok: true,
          reason: "already_reviewed",
        }
      )

      return {
        optimisticReviewId: optimisticReview.id,
        previousPermission,
        previousReviews,
      }
    },
    onError: (error, _input, context) => {
      restoreQueryData(reviewsQueryOptions.queryKey, context?.previousReviews)
      restoreQueryData(
        permissionQueryOptions.queryKey,
        context?.previousPermission
      )

      toast.error(getReviewSubmitErrorMessage(error))
    },
    onSuccess: (result, _input, context) => {
      if (!(result.ok && result.review)) {
        restoreQueryData(reviewsQueryOptions.queryKey, context?.previousReviews)
        restoreQueryData(
          permissionQueryOptions.queryKey,
          context?.previousPermission
        )

        toast.error(result.message ?? REVIEW_SUBMIT_ERROR_MESSAGE)
        return
      }

      toast.success(
        "Sua avaliacao foi enviada e sera publicada apos a aprovacao."
      )

      // Replace optimistic review with the actual pending review from server
      queryClient.setQueryData<ProductReviewsResponse>(
        reviewsQueryOptions.queryKey,
        (current) => {
          if (!(current?.ok && result.review)) {
            return current
          }

          const serverReview: ProductReviewItem = {
            ...result.review,
            isPending: true,
          }

          const nextReviews = current.reviews.map((review) =>
            review.id === context?.optimisticReviewId ? serverReview : review
          )

          return {
            ...current,
            reviews: nextReviews,
            summary: calculateReviewsSummary(
              nextReviews.filter((r) => !r.isPending)
            ),
          }
        }
      )
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: reviewsQueryOptions.queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: permissionQueryOptions.queryKey,
        }),
      ])
    },
  })

  const handleReviewSubmit = async (
    input: CreateReviewInput
  ): Promise<void> => {
    if (createReview.isPending) {
      return
    }

    await createReview.mutateAsync(input)
  }

  if (reviewsQuery.isPending) {
    return (
      <div className='rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 px-6 py-12 text-center text-muted-foreground text-sm'>
        Carregando avaliacoes...
      </div>
    )
  }

  if (reviewsQuery.isError || !reviewsQuery.data?.ok) {
    return (
      <div className='rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 px-6 py-12 text-center text-muted-foreground text-sm'>
        Nao foi possivel carregar as avaliacoes deste produto.
      </div>
    )
  }

  const reviews = reviewsQuery.data.reviews as PendingProductReviewItem[]
  const canReview = permissionQuery.data?.ok && permissionQuery.data.canReview

  return (
    <section className='space-y-8'>
      {canReview ? (
        <ReviewComposer
          autoFocus={writeReview}
          disabled={createReview.isPending}
          onSubmit={handleReviewSubmit}
          productId={productId}
          sessionCookie={sessionCookie}
        />
      ) : null}

      {reviews.length > 0 ? (
        <>
          <ReviewsSummary summary={reviewsQuery.data.summary} />

          <div>
            <h3 className='mb-4 font-medium'>
              {reviews.length} Avaliacoes deste Produto
            </h3>

            {reviews.map((review) => (
              <div className='space-y-2' key={review.id}>
                {review.isPending ? (
                  <p className='font-medium text-muted-foreground text-sm'>
                    Aguardando aprovacao...
                  </p>
                ) : null}
                <ReviewItem review={review as ProductReviewItem} />
              </div>
            ))}
          </div>
        </>
      ) : (
        <ReviewsEmptyState />
      )}
    </section>
  )
}
